// Astrea Ops Platform - Projects data-access layer (core project record)
//
// Phase 3, fourth slice - the biggest one. The app treats a project as one deeply
// nested object: core identity/status fields sit alongside the task board
// (groups/items/subtasks), personnel/assignments, proposal, financials, incidents,
// event form (~100 fields), debrief, activity log, and more. Phase 1's schema
// already split all of that into a dozen-plus normalized tables with real foreign
// keys (see supabase/migrations/20260915000003/4/5), but wiring every nested piece
// to its own table is its own slice, one area at a time (Tasks, Proposal,
// Financials, Event Form, Debrief, Personnel) - same pattern as Database, Team,
// and Unavailability before it. Per Martin's call on scope (16 September 2026):
// get the core project record onto Supabase now, convert each nested area later.
//
// The bridge: `projects.app_data` (jsonb, added in
// 20260915000013_projects_app_data_bridge.sql) holds the complete project object
// exactly as the app produces it - everything, including redundant copies of the
// fields below - so nothing is silently lost the moment `projects` stops reading
// from localStorage. The columns on `projects` are the authoritative, queryable
// source of truth for the fields mapped below (identity, classification, status,
// org/venue/people links, sector, location, dates, the attention flag).
// rowToEntry() always prefers the real column over whatever's sitting in app_data
// for those specific fields. As each nested area gets its own slice, that piece
// moves out of app_data into its real table and this column shrinks - it should
// end up empty, not grow into a second permanent copy of the schema.
//
// Personnel/assignments (fifth slice, following this same day): the `personnel`
// array is the first nested area to move out of app_data. loadProjects() now
// also loads every row from the `assignments` table (src/lib/assignments.js) and
// overlays each project's `personnel` field with its real assignment rows,
// falling back to whatever's in app_data only for a project with no assignment
// rows yet (covers a project that hasn't been touched since this slice landed).
// App.jsx's PersonnelPanel writes directly to `assignments` now - see
// assignments.js - so app_data.personnel is stale the moment a project is edited
// and should be treated as read-only fallback data going forward.
//
// Task board (sixth slice): `groups` is the second nested area to partly leave
// app_data - scoped down to the core kanban fields only (see src/lib/tasks.js's
// header for why). loadProjects() also loads every task_groups/tasks row
// (loadTaskGroupsByProject) and merges it onto each project's groups via
// mergeTaskGroups(): the real rows decide which groups/tasks exist and their
// name/status/assigneeId/dueDate, everything else not yet normalized (notes,
// subtasks, comments, Blocked By, Lead Checklist sections) still comes from
// app_data, matched by id.
//
// project_type: the app signals "this is a training" purely via the isTraining
// boolean, never by setting project.projectType to "training" (that field is only
// ever undefined/"event", "prospect", "schools_assessment", "assessment", or
// "consultation" - see makeTemplate/makeTrainingTemplate/makeProspectTemplate/
// makePlaceholderTemplate in App.jsx). The project_type enum DOES have a
// 'training' value, so it's used as the real column's value for a training
// project, but never written back into the reconstructed object's `projectType`
// field - only into `isTraining`, to match what the app itself would have set.
//
// organisation_id/venue_id/admin_id/manager_id are foreign keys - the app uses ""
// for "not set" where Postgres needs null for a uuid column, so every write
// converts "" -> null and every read converts null -> "".
//
// Ids: same convention as database.js/people.js/unavailability.js - every place
// in App.jsx that creates a new top-level project id now uses crypto.randomUUID()
// instead of the app's own uid() (a short Math.random() string, not a valid
// Postgres uuid). Nested ids not yet normalized (groups/tasks/subtasks etc.)
// still use uid() exactly as before - they live inside app_data jsonb until
// their own slice, so the id format doesn't matter yet.

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';
import { loadAssignmentsByProject } from './assignments';
import { loadTaskGroupsByProject, mergeTaskGroups } from './tasks';
import { loadEventFormsByProject, mergeEventForm } from './eventForm';
import { loadFinancialsByProject, mergeFinancials } from './financials';
import { loadProposalsByProject, mergeProposal } from './proposals';
import { mergeFinance, stripFinance, reapplyPublicProposal } from './financeSplit';

import { loadProjectMessagesByProject } from './messages';

// v7.63 (7 Oct 2026): whether the person looking may see money - Senior Management or
// Finance, and not a System Admin emulating another role. App sets this on every render.
// The database already refuses project_finance to everyone else; this decides whether
// to ask for it, and makes sure nothing financial is kept in memory otherwise.
let FINANCE_VIEW = false;
export function setFinanceView(v) { FINANCE_VIEW = !!v; }

async function loadProjectFinance() {
  const { data, error } = await selectAllRes(() => supabase.from('project_finance').select('project_id, data').order('project_id'));
  if (error) throw error;
  return new Map((data || []).map(r => [r.project_id, r.data || {}]));
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function safeDate(s) {
  return typeof s === 'string' && ISO_DATE_RE.test(s) ? s : null;
}

function entryToRow(entry) {
  const isTraining = !!entry.isTraining;
  // 30 September 2026: project messages live in their own table (project_messages,
  // see src/lib/messages.js) and must never be copied into app_data - it is readable
  // by every internal user, and a whole-blob write could overwrite a message someone
  // else just posted. A database trigger strips the key too; this just avoids sending it.
  // _rev is the server's version number (projects.rev), never stored inside app_data.
  const { projectMessages, _rev, ...appData } = entry;
  return {
    id: entry.id,
    name: entry.name || '(untitled project)',
    project_type: isTraining ? 'training' : (entry.projectType || 'event'),
    status: entry.status || 'unconfirmed',
    is_draft: !!entry.isDraft,
    organisation_id: entry.clientId || null,
    venue_id: entry.venueId || null,
    legacy_client_text: entry.client || null,
    legacy_venue_text: entry.venue || null,
    admin_id: entry.adminId || null,
    manager_id: entry.managerId || null,
    sector: entry.sector || null,
    location: entry.location || null,
    event_dates: entry.eventDates || null,
    event_end_date: safeDate(entry.eventEndDate),
    rough_dates: entry.roughDates || null,
    attention_flag: !!entry.attentionFlag,
    attention_note: entry.attentionNote || null,
    app_data: appData,
  };
}

function rowToEntry(row) {
  const base = row.app_data || {};
  const isTraining = row.project_type === 'training';
  return {
    ...base,
    id: row.id,
    name: row.name,
    status: row.status,
    isDraft: row.is_draft,
    isTraining,
    projectType: isTraining ? base.projectType : row.project_type,
    clientId: row.organisation_id || '',
    venueId: row.venue_id || '',
    client: row.legacy_client_text ?? base.client ?? '',
    venue: row.legacy_venue_text ?? base.venue ?? '',
    adminId: row.admin_id || '',
    managerId: row.manager_id || '',
    sector: row.sector ?? base.sector ?? '',
    location: row.location ?? base.location ?? '',
    eventDates: row.event_dates ?? base.eventDates ?? '',
    eventEndDate: row.event_end_date ?? base.eventEndDate ?? '',
    roughDates: row.rough_dates ?? base.roughDates ?? '',
    attentionFlag: row.attention_flag,
    // Version number for conflict detection (20261003000002_project_live_sync.sql); null
    // until that migration has been run.
    _rev: typeof row.rev === 'number' ? row.rev : (row.rev != null && !isNaN(Number(row.rev)) ? Number(row.rev) : null),
    attentionNote: row.attention_note ?? base.attentionNote ?? '',
    createdAt: row.created_at,
    // Defensive defaults for the nested areas not yet normalized - protects
    // against a future row that doesn't carry a full app_data blob (e.g. a
    // later Monday.com import into `projects` directly) crashing ProjectCard/
    // ProjectBoard, which read several of these without an ?. guard.
    groups: base.groups || [],
    personnel: base.personnel || [],
    incidents: base.incidents || [],
    activityLog: base.activityLog || [],
    vendorIds: base.vendorIds || [],
    files: base.files || [],
    projectMessages: [],
  };
}

export async function loadProjects() {
  // v7.64: a "deleted" project is hidden here; only System Admins see it, via loadDeletedProjects().
  const { data, error } = await selectAllRes(() => supabase.from('projects').select('*').is('deleted_at', null).order('id'));
  if (error) throw error;
  const finance = FINANCE_VIEW;
  const entries = (data || []).map(rowToEntry);
  // Finance logins only. If it fails, nothing is marked as loaded, so the database keeps
  // what it has (see finance_merge in the migration) rather than taking a partial copy.
  let financeByProject = null;
  if (finance) {
    try { financeByProject = await loadProjectFinance(); }
    catch (err) {
      // Before the 20261007000001 migration has been run there is no project_finance table,
      // and app_data still holds every figure - that copy is complete, so carry on as loaded.
      // Any other failure: nothing is marked as loaded.
      const msg = String((err && err.message) || '');
      const missing = !!err && (err.code === '42P01' || err.code === 'PGRST205' || (/project_finance/.test(msg) && /(does not exist|Could not find)/i.test(msg)));
      if (missing) financeByProject = new Map();
      else { console.error('Failed to load project finance:', err); financeByProject = null; }
    }
  }
  const [assignmentsByProject, taskGroupsByProject, eventFormsByProject, financialsByProject, proposalsByProject, messagesByProject] = await Promise.all([
    loadAssignmentsByProject(),
    loadTaskGroupsByProject(),
    loadEventFormsByProject(),
    finance ? loadFinancialsByProject() : Promise.resolve(new Map()),
    finance ? loadProposalsByProject() : Promise.resolve(new Map()),
    // A failure here shouldn't stop projects loading at all - the Progress tab just
    // shows no team messages until the next successful refresh.
    loadProjectMessagesByProject().catch(err => { console.error('Failed to load project messages:', err); return new Map(); }),
  ]);
  entries.forEach(entry => {
    const rows = assignmentsByProject.get(entry.id);
    if (rows) entry.personnel = rows;
    entry.groups = mergeTaskGroups(entry.groups, taskGroupsByProject.get(entry.id));
    const ef = eventFormsByProject.get(entry.id);
    entry.eventForm = mergeEventForm(entry.eventForm, ef?.row, ef?.actionItems, ef?.debriefResponses);
    entry.projectMessages = messagesByProject.get(entry.id) || [];
  });
  if (!finance) return entries.map(stripFinance);
  return entries.map(entry => {
    const appProposal = entry.proposal;
    const merged = mergeFinance(entry, financeByProject ? financeByProject.get(entry.id) : null);
    merged.financials = mergeFinancials(merged.financials, financialsByProject.get(entry.id));
    merged.proposal = reapplyPublicProposal(mergeProposal(merged.proposal, proposalsByProject.get(entry.id)), appProposal);
    // Tells the database this copy carries the complete finance data (projects.app_data
    // trigger); stripped from app_data on save.
    if (financeByProject) merged._finLoaded = true;
    return merged;
  });
}

// entry must already carry a real UUID id (see the module note above).
export async function projectCreate(entry) {
  const row = entryToRow(entry);
  if (entry.createdAt) row.created_at = entry.createdAt;
  const { error } = await supabase.from('projects').insert(row);
  if (error) throw error;
}

// expectRev (3 Oct 2026): when given, the update only applies if the row is still at that
// version - i.e. nobody else has saved this project since it was loaded. If someone has,
// this throws an error with .conflict = true and the caller merges and retries. Returns the
// new version number (or null where the rev column does not exist yet).
export async function projectUpdate(entry, expectRev) {
  const { id, ...patch } = entryToRow(entry);
  if (typeof expectRev === 'number') {
    const { data, error } = await supabase.from('projects').update(patch).eq('id', id).eq('rev', expectRev).select('rev');
    if (!error) {
      if (!data || !data.length) { const e = new Error('Project changed by someone else'); e.conflict = true; throw e; }
      return Number(data[0].rev);
    }
    if (!String(error.message || '').includes('rev')) throw error;
  }
  const { data, error } = await supabase.from('projects').update(patch).eq('id', id).select('rev');
  if (error) {
    if (!String(error.message || '').includes('rev')) throw error;
    const { error: e2 } = await supabase.from('projects').update(patch).eq('id', id);
    if (e2) throw e2;
    return null;
  }
  return data && data.length && data[0].rev != null ? Number(data[0].rev) : null;
}

// Cheap check for what changed: every project's id and version number.
export async function loadProjectRevs() {
  const { data, error } = await selectAllRes(() => supabase.from('projects').select('id,rev').is('deleted_at', null).order('id'));
  if (error) throw error;
  return new Map((data || []).map(r => [r.id, r.rev == null ? null : Number(r.rev)]));
}

// 17 September 2026: the app only ever had "Archive" for a project (a status change,
// fully reversible via Restore) - no real delete. Martin asked for a way to remove
// junk/test projects (e.g. ones created while testing the app) entirely, not just hide
// them in the Archived tab. Every child table (tasks, personnel/assignments, event_form,
// financials, proposal, incidents, documents, etc) has `project_id ... references
// projects(id) on delete cascade` (confirmed by reading every migration's foreign keys
// directly) so one delete on `projects` is genuinely enough - Postgres removes
// everything hanging off it, nothing to clean up table-by-table here. This is
// permanent and unrecoverable, unlike Archive - the UI only exposes it to admins and
// makes them type the project's name to confirm (see the "Delete Permanently" button
// in ProjectBoard).
// v7.64: "delete" no longer removes the row (and everything cascading off it). It moves the
// project to Deleted, which only System Admins can see and restore. A real delete, if ever
// needed, is a deliberate step in the Supabase SQL editor. The database enforces who may do
// this (projects_guard_update) regardless of the UI.
export async function projectDelete(id) {
  const { error } = await supabase.from('projects').update({ deleted_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

// Bring a project back out of Deleted (System Admin only; enforced by the database).
export async function projectRestore(id) {
  const { error } = await supabase.from('projects').update({ deleted_at: null }).eq('id', id);
  if (error) throw error;
}

// The Deleted list (System Admin only - RLS returns nothing for anyone else). Names and
// dates only; enough to decide what to restore.
export async function loadDeletedProjects() {
  const { data, error } = await supabase
    .from('projects')
    .select('id, name, deleted_at, deleted_by, updated_at')
    .not('deleted_at', 'is', null)
    .order('deleted_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Saved versions of a project (Senior Management / System Admin - RLS enforces it).
export async function loadProjectVersions(projectId) {
  const { data, error } = await supabase
    .from('project_versions')
    .select('id, rev, name, size_bytes, saved_by, replaced_at, reason')
    .eq('project_id', projectId)
    .order('replaced_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// Restore one saved version (keeps the current one first, so it can be undone). Finance
// figures are not touched. Returns nothing; reload the project afterwards.
export async function restoreProjectVersion(versionId) {
  const { error } = await supabase.rpc('restore_project_version', { p_version_id: versionId });
  if (error) throw error;
}
